import { currencySymbol, formatCurrency } from '@/utils/formatCurrency';

export type BrandAdsMarketplace = 'amazon' | 'flipkart';

export type MarketplaceFilter = 'all' | 'amazon' | 'flipkart';

export type BrandAdsDay = {
  date: string;
  spend: number;
  sales: number;
  units: number;
  hasRows: boolean;
};

export type BrandAdsProduct = {
  name: string;
  key: string;
  spend: number;
  sales: number;
  units: number;
};

export type BrandAdsTacticId = 'brand' | 'competitor' | 'generic' | 'rest';

export type BrandAdsTactic = {
  date?: string;
  id: BrandAdsTacticId;
  spend: number;
  sales: number;
  units: number;
};

export type BrandAdsCampaign = {
  spend: number;
  sales: number;
};

export type BrandAdsPlatform = {
  marketplace: BrandAdsMarketplace;
  currency: string;
  label: string;
  days: BrandAdsDay[];
  products: BrandAdsProduct[];
  tactics: BrandAdsTactic[];
  campaigns: BrandAdsCampaign[];
  pauseCount: number;
  scaleCount: number;
  zeroSaleSpend: number;
};

export type BrandAdsReportPayload = {
  startDate: string;
  endDate: string;
  platforms: BrandAdsPlatform[];
};

export type SummaryPart = {
  text: string;
  strong?: boolean;
};

const MONEY_EPSILON = 0.005;

const TACTIC_ORDER: BrandAdsTacticId[] = ['brand', 'competitor', 'generic', 'rest'];

export const TACTIC_LABELS: Record<BrandAdsTacticId, string> = {
  brand: 'Brand',
  competitor: 'Competition',
  generic: 'Generic',
  rest: 'Rest',
};

export function roas(sales: number, spend: number): number | null {
  if (!(spend > MONEY_EPSILON) || !Number.isFinite(sales) || !Number.isFinite(spend)) return null;
  return sales / spend;
}

export function formatRoas(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}×`;
}

export function formatRoi(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function percentChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function formatDelta(value: number | null): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

export function formatUnits(value: number): string {
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);
}

export function formatShare(part: number, whole: number): string {
  if (!(whole > MONEY_EPSILON)) return '—';
  return `${((part / whole) * 100).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`;
}

export function formatReportMoney(value: number, currency: string, compact = false): string {
  return formatCurrency(
    value,
    { currencyCode: currency },
    compact
      ? { notation: 'compact', maximumFractionDigits: 1 }
      : { maximumFractionDigits: 0 }
  );
}

export function campaignActions(campaigns: BrandAdsCampaign[]): {
  pauseCount: number;
  scaleCount: number;
  zeroSaleSpend: number;
} {
  const spend = campaigns.reduce((sum, row) => sum + row.spend, 0);
  const sales = campaigns.reduce((sum, row) => sum + row.sales, 0);
  const windowRoas = roas(sales, spend);
  let pauseCount = 0;
  let scaleCount = 0;
  let zeroSaleSpend = 0;
  for (const row of campaigns) {
    if (row.spend > MONEY_EPSILON && row.sales <= MONEY_EPSILON) {
      pauseCount += 1;
      zeroSaleSpend += row.spend;
      continue;
    }
    if (row.sales <= MONEY_EPSILON) continue;
    if (
      row.spend <= MONEY_EPSILON ||
      (windowRoas != null && row.sales / row.spend + 1e-9 >= windowRoas)
    ) {
      scaleCount += 1;
    }
  }
  return { pauseCount, scaleCount, zeroSaleSpend };
}

export function headlineCurrency(platforms: BrandAdsPlatform[]): string | null {
  const codes = [
    ...new Set(platforms.map((platform) => platform.currency).filter((code) => code && code !== 'UNKNOWN')),
  ];
  if (codes.length === 0) return platforms[0]?.currency ?? null;
  if (codes.length === 1) return codes[0];
  if (codes.includes('INR')) return 'INR';
  return codes[0];
}

export function tablePlatforms(
  platforms: BrandAdsPlatform[],
  filter: MarketplaceFilter
): BrandAdsPlatform[] {
  return platforms.filter((platform) => filter === 'all' || platform.marketplace === filter);
}

/** Cards, charts, and breakdowns use one currency. Both prefers the Flipkart currency. */
export function headlinePlatforms(
  platforms: BrandAdsPlatform[],
  filter: MarketplaceFilter
): BrandAdsPlatform[] {
  const visible = tablePlatforms(platforms, filter);
  const anchor = filter === 'all' ? headlineCurrency(platforms) : headlineCurrency(visible);
  if (!anchor) return visible;
  const matched = visible.filter((platform) => platform.currency === anchor);
  return matched.length > 0 ? matched : visible.slice(0, 1);
}

export function reportTitle(brandName: string, filter: MarketplaceFilter): string {
  if (filter === 'amazon') return `${brandName} ads: Amazon`;
  if (filter === 'flipkart') return `${brandName} ads: Flipkart`;
  return `${brandName} ads: Amazon & Flipkart`;
}

const SNAP_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export function monthStart(iso: string): string {
  const [year, month] = iso.split('-');
  if (!year || month?.length !== 2) return iso;
  return `${year}-${month}-01`;
}

export function snapshotTitle(endDate: string): string {
  const [year, month, day] = endDate.split('-').map(Number);
  if (!year || !month || !day || month < 1 || month > 12) return 'MP Snap';
  const dd = String(day).padStart(2, '0');
  const mm = String(month).padStart(2, '0');
  return `MP Snap ${SNAP_MONTHS[month - 1]} MTD till ${dd}-${mm}-${year}`;
}

export function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function shiftDay(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function latestDayPair(days: BrandAdsDay[]): {
  latest: BrandAdsDay | null;
  previous: BrandAdsDay | null;
} {
  const latest = [...days].reverse().find((day) => day.hasRows) ?? null;
  if (!latest) return { latest: null, previous: null };
  const previous = days.find((day) => day.date === shiftDay(latest.date, -1)) ?? null;
  return { latest, previous };
}

export function windowLabel(platforms: BrandAdsPlatform[], filter: MarketplaceFilter): string {
  const days = platforms[0]?.days ?? [];
  if (days.length === 0) return '';
  const start = formatDay(days[0].date);
  const end = formatDay(days[days.length - 1].date);
  let label = start === end ? start : `${start} – ${end}`;
  if (filter === 'flipkart') return label;
  const flipkart = platforms.find((platform) => platform.marketplace === 'flipkart');
  const last = flipkart ? [...flipkart.days].reverse().find((day) => day.hasRows) : null;
  const windowEnd = days[days.length - 1].date;
  if (last && last.date < windowEnd) {
    label += ` (Flipkart to ${formatDay(last.date)})`;
  }
  return label;
}

export function mergeDays(platforms: BrandAdsPlatform[]): BrandAdsDay[] {
  const byDate = new Map<string, BrandAdsDay>();
  for (const platform of platforms) {
    for (const day of platform.days) {
      const current = byDate.get(day.date) ?? {
        date: day.date,
        spend: 0,
        sales: 0,
        units: 0,
        hasRows: false,
      };
      current.spend += day.spend;
      current.sales += day.sales;
      current.units += day.units;
      current.hasRows = current.hasRows || day.hasRows;
      byDate.set(day.date, current);
    }
  }
  return [...byDate.values()].sort((left, right) => left.date.localeCompare(right.date));
}

export function mergeProducts(platforms: BrandAdsPlatform[]): BrandAdsProduct[] {
  const byKey = new Map<string, BrandAdsProduct>();
  for (const platform of platforms) {
    for (const product of platform.products) {
      const current = byKey.get(product.key);
      if (!current) {
        byKey.set(product.key, { ...product });
        continue;
      }
      current.spend += product.spend;
      current.sales += product.sales;
      current.units += product.units;
    }
  }
  return [...byKey.values()];
}

/** Sums rows that share a date and tag. Callers pass one currency. */
export function mergeTactics(platforms: BrandAdsPlatform[]): BrandAdsTactic[] {
  const byKey = new Map<string, BrandAdsTactic>();
  for (const platform of platforms) {
    for (const tactic of platform.tactics) {
      const key = `${tactic.date ?? ''}:${tactic.id}`;
      const current = byKey.get(key);
      if (!current) {
        byKey.set(key, { ...tactic });
        continue;
      }
      current.spend += tactic.spend;
      current.sales += tactic.sales;
      current.units += tactic.units;
    }
  }
  return [...byKey.values()].sort((left, right) => {
    const byDate = (left.date ?? '').localeCompare(right.date ?? '');
    if (byDate !== 0) return byDate;
    return TACTIC_ORDER.indexOf(left.id) - TACTIC_ORDER.indexOf(right.id);
  });
}

export function visibleTactics(tactics: BrandAdsTactic[]): BrandAdsTactic[] {
  return tactics.filter((row) => {
    if (row.id === 'rest') return row.spend > MONEY_EPSILON;
    return row.spend > MONEY_EPSILON || row.sales > MONEY_EPSILON;
  });
}

export function sumDays(days: BrandAdsDay[]): { spend: number; sales: number; units: number } {
  return days.reduce(
    (total, day) => ({
      spend: total.spend + day.spend,
      sales: total.sales + day.sales,
      units: total.units + day.units,
    }),
    { spend: 0, sales: 0, units: 0 }
  );
}

export type SnapshotTotals = {
  spend: number;
  sales: number;
  units: number;
  hasRows: boolean;
};

export type PlatformSnapshot = {
  yesterday: SnapshotTotals;
  mtd: SnapshotTotals;
};

const EMPTY_SNAPSHOT: SnapshotTotals = { spend: 0, sales: 0, units: 0, hasRows: false };

/** Yesterday is the selected end date. MTD sums days with rows from the 1st through that date. */
export function platformSnapshot(platform: BrandAdsPlatform, endDate: string): PlatformSnapshot {
  const start = monthStart(endDate);
  const yesterday = platform.days.find((day) => day.date === endDate);
  const mtdDays = platform.days.filter((day) => day.date >= start && day.date <= endDate && day.hasRows);
  const summed = sumDays(mtdDays);
  return {
    yesterday: yesterday?.hasRows
      ? { spend: yesterday.spend, sales: yesterday.sales, units: yesterday.units, hasRows: true }
      : EMPTY_SNAPSHOT,
    mtd: { spend: summed.spend, sales: summed.sales, units: summed.units, hasRows: mtdDays.length > 0 },
  };
}

export function summaryParts(platforms: BrandAdsPlatform[]): SummaryPart[] {
  const active = platforms.filter((platform) => sumDays(platform.days).spend > MONEY_EPSILON);
  if (active.length === 0) {
    return [{ text: 'No ad spend in this window.' }];
  }
  const currency = active[0].currency;
  const symbol = currencySymbol({ currencyCode: currency }) ?? '';
  const parts: SummaryPart[] = [];
  active.forEach((platform, index) => {
    const metrics = sumDays(platform.days);
    const value = formatRoas(roas(metrics.sales, metrics.spend));
    if (index === 0) {
      parts.push({ text: `${platform.label} returned ` });
      parts.push({ text: value, strong: true });
      parts.push({ text: ` in ad sales per ${symbol}1 spent` });
    } else {
      parts.push({ text: `, ${platform.label} ` });
      parts.push({ text: value, strong: true });
    }
  });
  parts.push({ text: '. ' });
  const campaigns = active.flatMap((platform) => platform.campaigns);
  const actions = campaignActions(campaigns);
  const spend = active.reduce((sum, platform) => sum + sumDays(platform.days).spend, 0);
  parts.push({ text: formatReportMoney(actions.zeroSaleSpend, currency), strong: true });
  parts.push({ text: ' (' });
  parts.push({ text: formatShare(actions.zeroSaleSpend, spend), strong: true });
  parts.push({ text: ' of spend) went to campaigns with no sales — ' });
  parts.push({ text: String(actions.pauseCount), strong: true });
  parts.push({
    text: actions.pauseCount === 1 ? ' campaign to pause and ' : ' campaigns to pause and ',
  });
  parts.push({ text: String(actions.scaleCount), strong: true });
  parts.push({ text: ' to scale.' });
  return parts;
}

export function summaryText(platforms: BrandAdsPlatform[]): string {
  return summaryParts(platforms)
    .map((part) => part.text)
    .join('');
}
